<script setup lang="ts">
import { capitalize } from "@/helpers/string";

import type { IRegularAvailabilities } from '@/types/worker';

import Checkbox from "@/dsComponents/checkbox/Checkbox.vue";

const momentKeys: (keyof IRegularAvailabilities)[] = ["morning", "afternoon", "evening", "night"];

interface AvailabilityRowProps {
    availability: IRegularAvailabilities;
    edition?: boolean;
    day: string;
    compact?: boolean;
}

const props = withDefaults(defineProps<AvailabilityRowProps>(), {
    compact: false,
});

const emit = defineEmits(["input"]);

const changeValue = (dayMoment: keyof IRegularAvailabilities) => {
    emit("input", { ...props.availability, [dayMoment]: !props.availability[dayMoment] });
};

const handleToggleRow = () => {
    const isAllChecked = momentKeys.every((key) => props.availability[key]);
    const newValue = !isAllChecked;
    emit("input", { morning: newValue, afternoon: newValue, evening: newValue, night: newValue });
};

const isChecked = (moment: keyof IRegularAvailabilities) => props.availability[moment];
</script>
<template>
    <div class="availability-row" :class="{ 'availability-row--compact': compact }">
        <div class="day-row">
            <div
                class="span-day"
                :class="{ 'typo-xxs-medium': compact, 'span-day--clickable': edition }"
                @click="edition && handleToggleRow()"
            >
                {{ capitalize(day) }}
            </div>
            <div class="span-moment" v-for="moment in momentKeys" :key="moment">
                <div class="span-moment__hit">
                    <Checkbox v-if="edition" :checked="isChecked(moment)" size="sm" @input="changeValue(moment)" />
                    <i v-else-if="isChecked(moment)" class="ti ti-check"></i>
                    <i v-else class="ti ti-x"></i>
                </div>
            </div>
        </div>
    </div>
</template>
<style scoped lang="scss">
.availability-row {
    border-top: 1px solid $stroke-soft-200;
    .day-row {
        height: 32px;
        display: grid;
        grid-template-columns: repeat(5, 1fr);
        align-items: center;

        .span-day {
            color: $text-sub-600;
            font-weight: 500;
            grid-column: auto / span 1;

            &--clickable {
                cursor: pointer;
                user-select: none;
            }
        }

        .span-moment {
            display: flex;
            justify-content: center;
            grid-column: auto / span 1;
        }

        i.ti.ti-check {
            color: $blue-base;
        }

        i.ti.ti-x {
            color: $icon-disabled-300;
        }

        .ti {
            font-size: 20px;
        }
    }

    &--compact {
        border-top: none;

        .day-row {
            height: 36px;
            grid-template-columns: 128px repeat(4, minmax(0, 1fr));
            padding: 0 var(--spacing-md, 8px);
            box-sizing: border-box;

            .span-day {
                display: flex;
                align-items: center;
                align-self: stretch;
                padding: 0 var(--spacing-xs, 4px);
                color: var(--text-main-900, #00061a);
            }
        }

        .span-moment {
            padding: 0 var(--spacing-xs, 4px);
        }

        .span-moment__hit {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 24px;
            height: 24px;
        }

        .day-row .ti {
            font-size: 20px;
        }
    }
}
</style>
